<script setup>
import { reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CompanyNoteItem from './components/CompanyNoteItem.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const state = reactive({
  message: '',
});

const currentUser = useMapGetter('getCurrentUser');
const notesByCompany = useMapGetter('companyNotes/getAllNotesByCompanyId');
const uiFlags = useMapGetter('companyNotes/getUIFlags');
const isFetchingNotes = computed(() => uiFlags.value.isFetching);
const isCreatingNote = computed(() => uiFlags.value.isCreating);
const notes = computed(() => notesByCompany.value(route.params.companyId));

const getWrittenBy = note => {
  const isCurrentUser = note?.user?.id === currentUser.value.id;
  return isCurrentUser
    ? t('COMPANY_DETAILS.SIDEBAR.NOTES.YOU')
    : note?.user?.name || 'Bot';
};

const onAdd = content => {
  if (!content) return;
  const { companyId } = route.params;
  store.dispatch('companyNotes/create', { content, companyId });
  state.message = '';
};

const onUpdate = (noteId, content) => {
  if (!noteId || !content) return;
  const { companyId } = route.params;
  store.dispatch('companyNotes/update', { noteId, content, companyId });
};

const onDelete = noteId => {
  if (!noteId) return;
  const { companyId } = route.params;
  store.dispatch('companyNotes/delete', { noteId, companyId });
};

const fetchNotes = () => {
  const { companyId } = route.params;
  if (companyId) {
    store.dispatch('companyNotes/get', { companyId });
  }
};

const keyboardEvents = {
  '$mod+Enter': {
    action: () => onAdd(state.message),
    allowOnFocusedInput: true,
  },
};
useKeyboardEvents(keyboardEvents);

onMounted(fetchNotes);

defineExpose({ fetchNotes });
</script>

<template>
  <div class="flex flex-col gap-6 py-6">
    <Editor
      v-model="state.message"
      :placeholder="t('COMPANY_DETAILS.SIDEBAR.NOTES.PLACEHOLDER')"
      focus-on-mount
      class="[&>div]:!border-transparent [&>div]:px-4 [&>div]:py-4 px-6"
    >
      <template #actions>
        <div class="flex items-center gap-3">
          <Button
            variant="link"
            color="blue"
            size="sm"
            :label="t('COMPANY_DETAILS.SIDEBAR.NOTES.SAVE')"
            class="hover:no-underline"
            :is-loading="isCreatingNote"
            :disabled="!state.message || isCreatingNote"
            @click="onAdd(state.message)"
          />
        </div>
      </template>
    </Editor>
    <div
      v-if="isFetchingNotes"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div v-else-if="notes.length > 0">
      <CompanyNoteItem
        v-for="note in notes"
        :key="note.id"
        class="mx-6 py-4"
        :note="note"
        :written-by="getWrittenBy(note)"
        allow-delete
        allow-edit
        @delete="onDelete"
        @update="onUpdate"
      />
    </div>
    <p v-else class="px-6 py-6 text-sm leading-6 text-center text-n-slate-11">
      {{ t('COMPANY_DETAILS.SIDEBAR.NOTES.EMPTY_STATE') }}
    </p>
  </div>
</template>
